import { useEffect } from 'react';
import { useMutation, UseMutationOptions, UseMutationResult } from '@tanstack/react-query';
import { useMessageStore } from '../zustandStore/messageStore';
import useLoadingStore from '../zustandStore/loadingStateStore';
import ApiError from '../lib/ApiError';
import { ApiResultMessages } from './enums';

interface CustomMutationOptions<TData, TError, TVariables, TContext> extends UseMutationOptions<
	TData,
	TError,
	TVariables,
	TContext
> {
	hideLoading?: boolean;
	showRetryMessageIfError?: boolean;
}

const useCustomMutation = <TData = unknown, TError = Error, TVariables = void, TContext = unknown>(
	options: CustomMutationOptions<TData, TError, TVariables, TContext>
): UseMutationResult<TData, TError, TVariables, TContext> => {
	const updateMessage = useMessageStore((state) => state.updateMessage);
	const setLoading = useLoadingStore((state) => state.setLoading);

	const mutationResult = useMutation(options);

	const { isPending, error } = mutationResult;

	useEffect(() => {
		if (options.hideLoading) {
			return;
		}
		setLoading(isPending);
	}, [isPending, options.hideLoading, setLoading]);

	useEffect(() => {
		if (error) {
			// Ignore AbortError - this occurs when the user navigates away before an async operation completes
			const errorName = (error as Error).name;
			const abortCheckMessage = (error as Error).message ?? '';
			if (errorName === 'AbortError' || abortCheckMessage.includes('AbortError')) return;

			const errorMessage =
				(error as ApiError).body ?? (error as ApiError).message ?? error?.toString() ?? 'An error occurred';

			if (options.showRetryMessageIfError) {
				updateMessage(ApiResultMessages.GeneralRetryMessage);
			} else {
				updateMessage(errorMessage);
			}
		}
	}, [error, updateMessage, options.showRetryMessageIfError]);

	return mutationResult;
};

export default useCustomMutation;
